/**
 * CORE-P-01 PMax tab — phase A.
 *
 * Chans's 2026-09-15 rebuild spec §9: PMax gets its own left-nav tab
 * with all PMax settings inside. "It's its own animal" — the blended
 * cost-per-conversion across Search / Display / YouTube / Discover /
 * Maps means PMax cannot be measured with the same tables as Search
 * (see getPmaxChannels module comment).
 *
 * This ticket also implements CORE-P-02: Placement Review is absorbed
 * into PMax. Its standalone nav entry retires; the old
 * `/ads-tools/placement-review` route redirects here.
 *
 * Phase A ships what the existing services already return:
 *
 *   - Channels breakdown (Where PMax actually spent) — via
 *     getPmaxChannels.
 *   - Placement Review (mobile-app, kids, gaming, gambling and
 *     wrong-geo / wrong-language placements) — via the existing
 *     PlacementReviewTab, wrapped as an inline section.
 *
 * Not in this PR (phase B, CORE-P-01.b): asset groups, themes,
 * signals — backend endpoints do not exist yet on origin/dev.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { PlacementReviewTab } from '@/components/google-ads/PlacementReviewTab';
import {
  getPmaxChannels,
  type PmaxChannelResponse,
  type PmaxChannelRow,
} from '@/services/googleAdsManagementService';
import { useTenantStore } from '@/stores/useTenantStore';

interface PmaxPageProps {
  tenantId?: string | null;
}

// CORE-P-01.c: window selector. Same contract as CORE-W-03 on the
// WhatConverts page: `?days=N` pins the choice on refresh + shared links,
// the default is dropped from the URL, unknown values fall back to the
// default rather than pinning a broken fetch. The backend route accepts
// 1..90, so 7 / 30 / 90 are the offered stops.
export type PmaxDaysRange = 7 | 30 | 90;
const PMAX_DAYS_OPTIONS: readonly { value: PmaxDaysRange; label: string }[] = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];
const PMAX_DAYS_URL_PARAM = 'days';
const DEFAULT_PMAX_DAYS: PmaxDaysRange = 30;

function parsePmaxDaysParam(raw: string | null | undefined): PmaxDaysRange | null {
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return PMAX_DAYS_OPTIONS.find((o) => o.value === n)?.value ?? null;
}

function usePmaxChannels(tenantId: string | null, days: PmaxDaysRange) {
  return useQuery<PmaxChannelResponse>({
    // `days` is in the key so a window change refetches.
    queryKey: ['pmax-channels', tenantId, days],
    queryFn: () => getPmaxChannels(tenantId ?? '', days),
    enabled: Boolean(tenantId),
    staleTime: 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });
}

function formatUsd(v: number): string {
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function formatConversions(v: number): string {
  return v.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function formatCount(v: number): string {
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/** CPA reads "—" when nothing converted; a $0 CPA would be a lie. */
function formatCpa(cost: number, conversions: number): string {
  return conversions > 0 ? formatUsd(cost / conversions) : '—';
}

function WindowSelector({
  value,
  onChange,
}: {
  value: PmaxDaysRange;
  onChange: (next: PmaxDaysRange) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Window"
      className="flex items-center gap-1 rounded-md border border-border p-1 text-xs"
    >
      {PMAX_DAYS_OPTIONS.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          className={
            'rounded px-2 py-1 font-medium ' +
            (value === o.value
              ? 'bg-coral text-white'
              : 'text-muted-foreground hover:text-foreground')
          }
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---- channels table ---------------------------------------------------------

type SortKey =
  | 'cost'
  | 'spend_share'
  | 'conversions'
  | 'cost_per_conversion'
  | 'clicks'
  | 'impressions';
type SortDir = 'asc' | 'desc';

const SORTABLE_COLUMNS: readonly { key: SortKey; label: string }[] = [
  { key: 'cost', label: 'Spend' },
  { key: 'spend_share', label: 'Share' },
  { key: 'conversions', label: 'Conversions' },
  { key: 'cost_per_conversion', label: 'CPA' },
  { key: 'clicks', label: 'Clicks' },
  { key: 'impressions', label: 'Impressions' },
];

/** Sort value for a row; CPA on a zero-conversion row is "none", sorted last either way. */
function sortValue(row: PmaxChannelRow, key: SortKey): number | null {
  if (key === 'cost_per_conversion') {
    return row.conversions > 0 ? row.cost_per_conversion : null;
  }
  return row[key];
}

function sortRows(
  rows: readonly PmaxChannelRow[],
  key: SortKey,
  dir: SortDir,
): PmaxChannelRow[] {
  const sign = dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    if (av === bv) return a.label.localeCompare(b.label);
    return (av - bv) * sign;
  });
}

function ShareBar({ share }: { share: number | null | undefined }) {
  // A row without a usable share (missing, NaN, Infinity) draws an empty
  // track and reads "—" rather than "NaN%".
  const known = typeof share === 'number' && Number.isFinite(share);
  const pct = known ? Math.max(0, Math.min(100, share)) : 0;
  return (
    <div className="flex items-center justify-end gap-2">
      <div
        className="h-2 w-24 shrink-0 overflow-hidden rounded-full bg-muted"
        aria-hidden="true"
      >
        <div
          data-testid="pmax-share-fill"
          className="h-full rounded-full bg-coral"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-10 text-right tabular-nums">
        {known ? `${share.toFixed(0)}%` : '—'}
      </span>
    </div>
  );
}

function ChannelsTable({
  rows,
  days,
}: {
  rows: readonly PmaxChannelRow[];
  days: PmaxDaysRange;
}) {
  const [sortKey, setSortKey] = useState<SortKey>('cost');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const sorted = useMemo(() => sortRows(rows, sortKey, sortDir), [rows, sortKey, sortDir]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          cost: acc.cost + r.cost,
          conversions: acc.conversions + r.conversions,
          clicks: acc.clicks + r.clicks,
          impressions: acc.impressions + r.impressions,
        }),
        { cost: 0, conversions: 0, clicks: 0, impressions: 0 },
      ),
    [rows],
  );

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No Performance Max delivery in the last {days} days.
      </p>
    );
  }

  // Click on the active column flips direction; on another column sorts
  // it descending first (the "most" is what an operator looks for).
  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] border-collapse text-sm">
        <thead className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
          <tr>
            <th scope="col" className="px-3 py-2 text-left font-semibold">
              Channel
            </th>
            {SORTABLE_COLUMNS.map((col) => {
              const active = col.key === sortKey;
              const Icon = active ? (sortDir === 'desc' ? ArrowDown : ArrowUp) : ArrowUpDown;
              return (
                <th
                  key={col.key}
                  scope="col"
                  className="px-3 py-2 text-right font-semibold"
                  aria-sort={active ? (sortDir === 'desc' ? 'descending' : 'ascending') : 'none'}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(col.key)}
                    className={
                      'inline-flex items-center gap-1 uppercase tracking-wide hover:text-foreground ' +
                      (active ? 'text-foreground' : '')
                    }
                  >
                    {col.label}
                    <Icon className="h-3 w-3" aria-hidden="true" />
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr
              key={r.channel}
              className="border-b border-border/60 hover:bg-muted/20"
            >
              <td className="px-3 py-2 font-medium">{r.label}</td>
              <td className="px-3 py-2 text-right tabular-nums">{formatUsd(r.cost)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                <ShareBar share={r.spend_share} />
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {formatConversions(r.conversions)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {formatCpa(r.cost, r.conversions)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{formatCount(r.clicks)}</td>
              <td className="px-3 py-2 text-right tabular-nums">
                {formatCount(r.impressions)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr data-testid="pmax-totals-row" className="border-t border-border font-semibold">
            <th scope="row" className="px-3 py-2 text-left">
              Total
            </th>
            <td className="px-3 py-2 text-right tabular-nums">{formatUsd(totals.cost)}</td>
            <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
              100%
            </td>
            <td className="px-3 py-2 text-right tabular-nums">
              {formatConversions(totals.conversions)}
            </td>
            <td className="px-3 py-2 text-right tabular-nums">
              {formatCpa(totals.cost, totals.conversions)}
            </td>
            <td className="px-3 py-2 text-right tabular-nums">{formatCount(totals.clicks)}</td>
            <td className="px-3 py-2 text-right tabular-nums">
              {formatCount(totals.impressions)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export function PmaxPage({ tenantId: tenantIdProp }: PmaxPageProps) {
  const storeTenant = useTenantStore((s) => s.currentTenant);
  const tenantId = (tenantIdProp === undefined ? storeTenant : tenantIdProp) ?? null;

  // CORE-P-01.c: the URL is the single source of truth for the window
  // (derived every render, not copied into state at mount), so an in-app
  // ?days change — back button, a link — moves the selector too. Unknown
  // or absent -> 30 days.
  const [searchParams, setSearchParams] = useSearchParams();
  const days: PmaxDaysRange =
    parsePmaxDaysParam(searchParams.get(PMAX_DAYS_URL_PARAM)) ?? DEFAULT_PMAX_DAYS;
  const handleDaysChange = (value: PmaxDaysRange) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === DEFAULT_PMAX_DAYS) {
          next.delete(PMAX_DAYS_URL_PARAM);
        } else {
          next.set(PMAX_DAYS_URL_PARAM, String(value));
        }
        return next;
      },
      { replace: true },
    );
  };

  const channelsQuery = usePmaxChannels(tenantId, days);

  // CORE-P-01.c: purpose statement lives on the outer GoogleAdsOpsPage
  // wrapper (one H1 + description per Ads Tools section, sectionCopy.pmax).
  // No inline header here, so the operator reads the title once.
  if (!tenantId) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>No workspace selected</CardTitle>
            <CardDescription>
              Pick a workspace to see its Performance Max activity.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1.5">
              <CardTitle>Where PMax actually spent</CardTitle>
              <CardDescription>
                Google reports one blended cost-per-conversion across every PMax
                channel. This table splits the spend so an account that is 90%
                Display and 10% Search does not look like one 100% Search
                account.
              </CardDescription>
            </div>
            <WindowSelector value={days} onChange={handleDaysChange} />
          </div>
        </CardHeader>
        <CardContent>
          {channelsQuery.isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Reading PMax channels for {tenantId}...
            </div>
          )}
          {channelsQuery.isError && (
            <p className="text-sm text-muted-foreground">
              Could not read PMax channels for{' '}
              <span className="font-medium text-foreground">{tenantId}</span>.
              Check the connection on the Account Audit tab, then reload.
            </p>
          )}
          {channelsQuery.isSuccess && (
            <ChannelsTable rows={channelsQuery.data?.channels ?? []} days={days} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Placements</CardTitle>
          <CardDescription>
            Placements flagged as mobile-app, kids content, gaming, gambling,
            wrong geo, or wrong language, with the exclusion controls.
            Absorbed here from the standalone Placement Review page
            (CORE-P-02).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <PlacementReviewTab tenantId={tenantId} />
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Coming next (CORE-P-01.b): asset groups, themes, signals. The
        backend surfaces for those are not there yet on origin/dev.
      </p>
    </div>
  );
}

export default PmaxPage;
